import { useEffect, useMemo, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, Download, RefreshCw, GitCompare } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AdminST1Preview — Cluster 3 · Slice S4.
 *
 * Renders the TEST-MODE ST-1 bundle (.DAT / .CSV / .SCH) in-browser so an
 * admin can eyeball the eventual VA server-to-server payload before we
 * receive live credentials. Includes:
 *   - Verification-status table (verified | assumed | pending_va_reply)
 *   - Byte-diff view between two consecutive builds (fixes "did anything
 *     silently change?" during pilot)
 *   - Download link to the ZIP bundle
 *
 * Backing endpoint: GET /api/admin/tax/st1-testfile-preview?year=&month=
 */
export default function AdminST1Preview({ user, onLogout }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [current, setCurrent] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/tax/st1-testfile-preview', { params: { year, month } });
      setCurrent(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  const snapshotNow = () => {
    if (!current) return;
    setSnapshot(current);
    toast.success('Snapshot captured. Rerun to compare byte-by-byte.');
  };

  const downloadZip = async () => {
    try {
      const res = await api.get('/admin/tax/st1-testfile.zip', {
        params: { year, month },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dealshaq-st1-${year}-${String(month).padStart(2, '0')}-TESTMODE.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-st1-preview-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">ST-1 Preview · Test Mode</h1>
          <p className="text-sm text-slate-500 mt-1">
          Byte-level preview of the fixed-width VA Web Upload file before submission. Field widths
          marked <code className="bg-slate-100 px-1 rounded text-xs">pending_va_reply</code> stay
          frozen until the Virginia Department of Taxation returns the official layout guide.
          </p>
        </div>

        <Card>
          <CardHeader><CardTitle>Select period</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-end gap-4">
            <div>
              <Label htmlFor="year">Year</Label>
              <Input id="year" type="number" value={year}
                     onChange={(e) => setYear(parseInt(e.target.value || '0'))}
                     className="w-28" data-testid="st1preview-year-input" />
            </div>
            <div>
              <Label htmlFor="month">Month</Label>
              <Input id="month" type="number" min="1" max="12" value={month}
                     onChange={(e) => setMonth(parseInt(e.target.value || '0'))}
                     className="w-24" data-testid="st1preview-month-input" />
            </div>
            <Button onClick={run} disabled={loading} data-testid="st1preview-run-button">
              <Eye className="w-4 h-4 mr-2" />
              {loading ? 'Building…' : 'Preview file'}
            </Button>
            {current && (
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={snapshotNow} data-testid="st1preview-snapshot-button">
                  <RefreshCw className="w-4 h-4 mr-2" />Snapshot for diff
                </Button>
                <Button variant="outline" onClick={downloadZip} data-testid="st1preview-download-button">
                  <Download className="w-4 h-4 mr-2" />ZIP
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {current && (
          <>
            <VerificationCard rows={current.verification} shaMap={current.sha256_by_file} />
            <Tabs defaultValue="dat">
              <TabsList>
                <TabsTrigger value="dat" data-testid="st1preview-tab-dat">.DAT (positional)</TabsTrigger>
                <TabsTrigger value="csv" data-testid="st1preview-tab-csv">.CSV (delimited)</TabsTrigger>
                <TabsTrigger value="sch" data-testid="st1preview-tab-sch">.SCH (schedule)</TabsTrigger>
                {snapshot && <TabsTrigger value="diff" data-testid="st1preview-tab-diff">
                  <GitCompare className="w-3 h-3 mr-1" />Byte diff
                </TabsTrigger>}
              </TabsList>
              <TabsContent value="dat">
                <FileViewer title=".DAT" text={current.dat_text} sha={current.sha256_by_file.dat}
                            testid="st1preview-dat" />
              </TabsContent>
              <TabsContent value="csv">
                <FileViewer title=".CSV" text={current.csv_text} sha={current.sha256_by_file.csv}
                            testid="st1preview-csv" />
              </TabsContent>
              <TabsContent value="sch">
                <FileViewer title=".SCH" text={current.sch_text} sha={current.sha256_by_file.sch}
                            testid="st1preview-sch" />
              </TabsContent>
              {snapshot && (
                <TabsContent value="diff">
                  <DiffView current={current} snapshot={snapshot} />
                </TabsContent>
              )}
            </Tabs>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function VerificationCard({ rows, shaMap }) {
  const summary = useMemo(() => {
    const counts = { verified: 0, assumed: 0, pending_va_reply: 0 };
    rows.forEach(r => { counts[r.status] = (counts[r.status] || 0) + 1; });
    return counts;
  }, [rows]);
  return (
    <Card data-testid="st1preview-verification-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Field verification status</CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Every field width labeled per the Formal Verification Protocol. No `pending_va_reply`
            row may ship to prod.
          </p>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-emerald-500">verified · {summary.verified}</Badge>
          <Badge className="bg-slate-500">assumed · {summary.assumed}</Badge>
          <Badge className="bg-amber-500">pending_va_reply · {summary.pending_va_reply}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 mb-3 font-mono text-xs">
          <div>DAT sha256: <span className="text-slate-500">{shaMap.dat.slice(0, 16)}…</span></div>
          <div>CSV sha256: <span className="text-slate-500">{shaMap.csv.slice(0, 16)}…</span></div>
          <div>SCH sha256: <span className="text-slate-500">{shaMap.sch.slice(0, 16)}…</span></div>
        </div>
        <table className="w-full text-xs">
          <thead className="text-left text-slate-500 border-b">
            <tr>
              <th className="py-1">Field</th><th>Width</th><th>Type</th>
              <th>Status</th><th>Citation</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1 font-mono">{r.field}</td>
                <td>{r.width}</td>
                <td>{r.type}</td>
                <td>
                  <Badge className={
                    r.status === 'verified' ? 'bg-emerald-500' :
                    r.status === 'assumed' ? 'bg-slate-500' : 'bg-amber-500'
                  }>{r.status}</Badge>
                </td>
                <td className="text-slate-500">{r.citation}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function FileViewer({ title, text, sha, testid }) {
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">{title} — sha256 <span className="font-mono text-xs text-slate-500">{sha.slice(0, 24)}…</span></CardTitle>
      </CardHeader>
      <CardContent>
        <pre
          className="bg-slate-950 text-slate-100 text-xs p-4 rounded max-h-[60vh] overflow-auto font-mono whitespace-pre"
          data-testid={`${testid}-viewer`}
        >{text || '(empty)'}</pre>
      </CardContent>
    </Card>
  );
}

function DiffView({ current, snapshot }) {
  const files = ['dat', 'csv', 'sch'];
  return (
    <Card className="mt-4" data-testid="st1preview-diff-card">
      <CardHeader>
        <CardTitle className="text-sm">Byte-diff — snapshot vs current</CardTitle>
        <p className="text-xs text-slate-500">
          Compares each file&apos;s SHA-256. Anything red means the fixed-width byte stream drifted since
          your snapshot. Investigate before submitting.
        </p>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-left">
            <tr><th>File</th><th>Snapshot SHA</th><th>Current SHA</th><th>Δ length</th><th>Status</th></tr>
          </thead>
          <tbody>
            {files.map(k => {
              const s = snapshot.sha256_by_file[k];
              const c = current.sha256_by_file[k];
              const equal = s === c;
              const dLen = (current[`${k}_text`]?.length ?? 0) - (snapshot[`${k}_text`]?.length ?? 0);
              return (
                <tr key={k} className="border-b border-slate-100">
                  <td className="py-1 font-mono uppercase">.{k}</td>
                  <td className="font-mono text-xs">{s.slice(0, 20)}…</td>
                  <td className="font-mono text-xs">{c.slice(0, 20)}…</td>
                  <td className="font-mono text-xs">{dLen >= 0 ? `+${dLen}` : dLen}</td>
                  <td>
                    <Badge className={equal ? 'bg-emerald-500' : 'bg-red-500'}>
                      {equal ? 'identical' : 'DRIFT'}
                    </Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
